import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";

// ─── Google Fonts ─────────────────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;1,500&family=Inter:wght@300;400;500&display=swap";
document.head.appendChild(fontLink);

// ─── CSS ─────────────────────────────────────────────────────────────────────
const css = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #1c1c1c;
    --paper: #f7f3ee;
    --warm: #f0ece5;
    --gold: #b8924a;
    --gold-muted: #d4b07a;
    --gold-faint: rgba(184,146,74,0.12);
    --muted: #888480;
    --white: #ffffff;
    --serif: 'Playfair Display', Georgia, serif;
    --sans: 'Inter', sans-serif;
  }

  html { scroll-behavior: smooth; }
  body { font-family: var(--sans); background: var(--paper); color: var(--ink); }

  /* NAV */
  .nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 1.4rem 3.5rem;
    background: rgba(247,243,238,0.93);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid rgba(184,146,74,0.18);
    transition: all 0.3s;
  }
  .nav.scrolled {
    padding: 1rem 3.5rem;
    background: rgba(28,28,28,0.97);
    border-bottom-color: rgba(184,146,74,0.25);
  }
  .nav-logo {
    font-family: var(--serif); font-size: 1.5rem; font-weight: 700;
    color: var(--ink); text-decoration: none; letter-spacing: 0.02em;
  }
  .nav-logo span { color: var(--gold); }
  .nav.scrolled .nav-logo { color: var(--paper); }
  .nav-links { display: flex; gap: 2rem; align-items: center; }
  .nav-links a {
    font-size: 0.78rem; font-weight: 400; letter-spacing: 0.1em;
    text-transform: uppercase; text-decoration: none; color: var(--ink);
    transition: color 0.2s;
  }
  .nav.scrolled .nav-links a { color: rgba(247,243,238,0.75); }
  .nav-links a:hover { color: var(--gold); }
  .nav.scrolled .nav-links a:hover { color: var(--gold); }
  .nav-cta {
    background: var(--ink) !important; color: var(--paper) !important;
    padding: 0.5rem 1.3rem !important; border-radius: 3px !important;
  }
  .nav.scrolled .nav-cta { background: var(--gold) !important; color: var(--ink) !important; }
  .nav-cta:hover { background: var(--gold) !important; color: var(--ink) !important; }

  /* HERO */
  .hero {
    min-height: 100vh;
    display: grid; grid-template-columns: 55% 45%;
    padding-top: 72px;
  }
  .hero-left {
    display: flex; flex-direction: column; justify-content: center;
    padding: 5rem 3.5rem 5rem 5rem;
  }
  .tag {
    font-size: 0.7rem; font-weight: 500; letter-spacing: 0.22em;
    text-transform: uppercase; color: var(--gold);
    display: flex; align-items: center; gap: 0.7rem; margin-bottom: 1.75rem;
  }
  .tag::before { content: ''; display: block; width: 1.8rem; height: 1px; background: var(--gold); }
  .hero-title {
    font-family: var(--serif); font-size: clamp(2.6rem, 4.5vw, 5rem);
    font-weight: 700; line-height: 1.08; color: var(--ink);
    margin-bottom: 1.75rem;
  }
  .hero-title em { font-style: italic; font-weight: 500; color: var(--gold); }
  .hero-body {
    font-size: 1rem; font-weight: 300; line-height: 1.85; color: #555;
    max-width: 480px; margin-bottom: 2.75rem;
  }
  .hero-actions { display: flex; gap: 1rem; flex-wrap: wrap; }
  .btn-dark {
    background: var(--ink); color: var(--paper);
    padding: 0.9rem 2rem; border: none; border-radius: 3px;
    font-family: var(--sans); font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase;
    cursor: pointer; transition: all 0.22s;
  }
  .btn-dark:hover { background: var(--gold); color: var(--ink); transform: translateY(-2px); }
  .btn-line {
    background: transparent; color: var(--ink);
    padding: 0.9rem 2rem; border: 1.5px solid var(--ink); border-radius: 3px;
    font-family: var(--sans); font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase;
    cursor: pointer; transition: all 0.22s;
  }
  .btn-line:hover { background: var(--ink); color: var(--paper); transform: translateY(-2px); }

  .hero-right {
    background: var(--ink); position: relative; overflow: hidden;
    display: flex; flex-direction: column; justify-content: center;
    padding: 4.5rem 3.5rem;
  }
  .hero-right::before {
    content: ''; position: absolute; bottom: -80px; right: -80px;
    width: 380px; height: 380px; border-radius: 50%;
    border: 1px solid rgba(184,146,74,0.12);
  }
  .hero-right::after {
    content: ''; position: absolute; bottom: -20px; right: -20px;
    width: 220px; height: 220px; border-radius: 50%;
    border: 1px solid rgba(184,146,74,0.18);
  }
  .hero-right-label {
    font-size: 0.68rem; font-weight: 500; letter-spacing: 0.22em;
    text-transform: uppercase; color: rgba(184,146,74,0.7);
    margin-bottom: 2.5rem; position: relative; z-index: 1;
  }
  .serve-group { position: relative; z-index: 1; margin-bottom: 2.5rem; }
  .serve-group-title {
    font-family: var(--serif); font-size: 1.3rem; font-weight: 500;
    color: var(--paper); margin-bottom: 1rem;
  }
  .serve-items { display: flex; flex-direction: column; gap: 0.45rem; }
  .serve-item {
    font-size: 0.85rem; font-weight: 300; color: rgba(247,243,238,0.6);
    display: flex; align-items: center; gap: 0.7rem;
  }
  .serve-item::before {
    content: ''; display: block; width: 0.9rem; height: 1px;
    background: var(--gold); flex-shrink: 0;
  }
  .serve-divider {
    border: none; border-top: 1px solid rgba(255,255,255,0.07);
    margin: 0 0 2.5rem; position: relative; z-index: 1;
  }

  /* MARQUEE STRIP */
  .strip { background: var(--gold); overflow: hidden; padding: 0.75rem 0; }
  .strip-inner {
    display: flex; gap: 3rem;
    animation: marquee 30s linear infinite;
    white-space: nowrap;
  }
  .strip-inner span {
    font-size: 0.7rem; font-weight: 500; letter-spacing: 0.18em;
    text-transform: uppercase; color: var(--ink); flex-shrink: 0;
  }
  .strip-inner .dot { opacity: 0.35; }
  @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

  /* ABOUT */
  .about-section {
    padding: 7rem 5rem;
    display: grid; grid-template-columns: 1fr 1fr; gap: 5rem; align-items: center;
  }
  .section-tag {
    font-size: 0.7rem; font-weight: 500; letter-spacing: 0.22em;
    text-transform: uppercase; color: var(--gold); margin-bottom: 1rem;
  }
  .section-title {
    font-family: var(--serif); font-size: clamp(1.9rem, 3.5vw, 3rem);
    font-weight: 700; line-height: 1.18; color: var(--ink); margin-bottom: 1.5rem;
  }
  .section-title em { font-style: italic; font-weight: 500; }
  .section-body {
    font-size: 0.95rem; font-weight: 300; line-height: 1.85; color: #555;
  }
  .mission-block {
    background: var(--ink); padding: 2.5rem;
    border-radius: 3px; position: relative; overflow: hidden;
  }
  .mission-block::before {
    content: ''; position: absolute; top: 0; left: 0;
    width: 4px; height: 100%; background: var(--gold);
  }
  .mission-quote {
    font-family: var(--serif); font-size: 1.25rem; font-style: italic;
    font-weight: 500; line-height: 1.65; color: var(--paper);
    padding-left: 1rem;
  }
  .mission-quote strong { color: var(--gold-muted); font-style: normal; font-weight: 500; }

  /* HOW IT WORKS */
  .how-section { padding: 7rem 5rem; background: var(--warm); }
  .how-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 2px; margin-top: 3.5rem;
  }
  .step-card {
    padding: 2.75rem 2.25rem;
    border: 1px solid rgba(28,28,28,0.09);
    background: var(--warm); transition: all 0.28s;
  }
  .step-card:hover { background: var(--ink); }
  .step-card:hover .step-num { color: rgba(184,146,74,0.5); }
  .step-card:hover .step-title { color: var(--paper); }
  .step-card:hover .step-text { color: rgba(247,243,238,0.55); }
  .step-num {
    font-family: var(--serif); font-size: 3.5rem; font-weight: 700;
    color: rgba(184,146,74,0.18); line-height: 1; margin-bottom: 1.5rem;
    transition: color 0.28s;
  }
  .step-line { width: 2rem; height: 2px; background: var(--gold); margin-bottom: 1.5rem; }
  .step-title {
    font-family: var(--serif); font-size: 1.3rem; font-weight: 700;
    color: var(--ink); margin-bottom: 0.75rem; transition: color 0.28s;
  }
  .step-text {
    font-size: 0.88rem; font-weight: 300; color: #666;
    line-height: 1.75; transition: color 0.28s;
  }

  /* WHO WE SERVE */
  .serve-section {
    padding: 7rem 5rem; background: var(--ink);
    position: relative; overflow: hidden;
  }
  .serve-section::before {
    content: ''; position: absolute; top: -120px; right: -120px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(184,146,74,0.07) 0%, transparent 70%);
  }
  .serve-grid {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 2rem; margin-top: 3.5rem; position: relative; z-index: 1;
  }
  .serve-card {
    padding: 3rem; border: 1px solid rgba(184,146,74,0.2);
    border-radius: 3px; position: relative;
  }
  .serve-card::after {
    content: ''; position: absolute; top: 0; left: 0;
    width: 3px; height: 100%; background: var(--gold);
    border-radius: 3px 0 0 3px;
  }
  .serve-role {
    font-size: 0.68rem; font-weight: 500; letter-spacing: 0.2em;
    text-transform: uppercase; color: var(--gold); margin-bottom: 0.75rem;
  }
  .serve-card-title {
    font-family: var(--serif); font-size: 1.65rem; font-weight: 700;
    color: var(--paper); margin-bottom: 1rem; line-height: 1.2;
  }
  .serve-card-body {
    font-size: 0.88rem; font-weight: 300; color: rgba(247,243,238,0.6);
    line-height: 1.8; margin-bottom: 1.5rem;
  }
  .serve-list { list-style: none; display: flex; flex-direction: column; gap: 0.5rem; }
  .serve-list li {
    font-size: 0.83rem; font-weight: 300; color: rgba(247,243,238,0.65);
    display: flex; align-items: center; gap: 0.7rem;
  }
  .serve-list li::before {
    content: ''; display: block; width: 0.9rem; height: 1px;
    background: var(--gold); flex-shrink: 0;
  }

  /* TESTIMONIALS */
  .testi-section { padding: 7rem 5rem; }
  .testi-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem; margin-top: 3.5rem;
  }
  .testi-card {
    background: var(--white); padding: 2.25rem; border-radius: 3px;
    border: 1px solid rgba(28,28,28,0.07); position: relative;
  }
  .testi-open {
    font-family: var(--serif); font-size: 5rem; font-weight: 700;
    color: rgba(184,146,74,0.15);
    position: absolute; top: -0.5rem; left: 1.25rem; line-height: 1;
  }
  .testi-text {
    font-family: var(--serif); font-size: 1rem; font-style: italic;
    line-height: 1.75; color: #333; margin-bottom: 1.5rem;
    position: relative; z-index: 1;
  }
  .testi-author { display: flex; align-items: center; gap: 0.85rem; }
  .testi-avatar {
    width: 40px; height: 40px; border-radius: 50%; background: var(--ink);
    display: flex; align-items: center; justify-content: center;
    font-size: 0.72rem; font-weight: 500; color: var(--gold-muted); flex-shrink: 0;
  }
  .testi-name { font-size: 0.88rem; font-weight: 500; color: var(--ink); }
  .testi-role { font-size: 0.78rem; color: var(--muted); }

  /* CTA */
  .cta-section {
    padding: 8rem 5rem; background: var(--ink);
    text-align: center; position: relative; overflow: hidden;
  }
  .cta-section::before {
    content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: 600px; height: 600px; border-radius: 50%;
    border: 1px solid rgba(184,146,74,0.08);
  }
  .cta-inner { position: relative; z-index: 1; max-width: 600px; margin: 0 auto; }
  .cta-title {
    font-family: var(--serif); font-size: clamp(2.2rem, 5vw, 4rem);
    font-weight: 700; line-height: 1.12; color: var(--paper); margin-bottom: 1.5rem;
  }
  .cta-title em { font-style: italic; font-weight: 500; color: var(--gold-muted); }
  .cta-body {
    font-size: 0.95rem; font-weight: 300; color: rgba(247,243,238,0.6);
    line-height: 1.85; margin-bottom: 2.75rem;
  }
  .cta-buttons { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
  .btn-gold {
    background: var(--gold); color: var(--ink);
    padding: 1rem 2.2rem; border: none; border-radius: 3px;
    font-family: var(--sans); font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase;
    cursor: pointer; transition: all 0.22s;
  }
  .btn-gold:hover { background: var(--gold-muted); transform: translateY(-2px); }
  .btn-ghost {
    background: transparent; color: var(--paper);
    padding: 1rem 2.2rem; border: 1.5px solid rgba(247,243,238,0.25); border-radius: 3px;
    font-family: var(--sans); font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase;
    cursor: pointer; transition: all 0.22s;
  }
  .btn-ghost:hover { border-color: var(--gold); color: var(--gold); transform: translateY(-2px); }

  /* FOOTER */
  .footer { background: #111; padding: 3.5rem 5rem 2.25rem; }
  .footer-top {
    display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 4rem;
    padding-bottom: 2.5rem;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    margin-bottom: 2rem;
  }
  .footer-logo {
    font-family: var(--serif); font-size: 1.6rem; font-weight: 700;
    color: var(--paper); margin-bottom: 0.85rem;
  }
  .footer-logo span { color: var(--gold); }
  .footer-tagline {
    font-size: 0.83rem; font-weight: 300; line-height: 1.7;
    color: rgba(247,243,238,0.38); max-width: 260px;
  }
  .footer-col-title {
    font-size: 0.68rem; font-weight: 500; letter-spacing: 0.2em;
    text-transform: uppercase; color: var(--gold); margin-bottom: 1.25rem;
  }
  .footer-links { list-style: none; display: flex; flex-direction: column; gap: 0.55rem; }
  .footer-links a {
    font-size: 0.83rem; font-weight: 300;
    color: rgba(247,243,238,0.45); text-decoration: none; transition: color 0.2s;
  }
  .footer-links a:hover { color: var(--gold); }
  .footer-bottom {
    display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;
  }
  .footer-copy { font-size: 0.78rem; font-weight: 300; color: rgba(247,243,238,0.3); }
  .footer-legal { display: flex; gap: 1.75rem; }
  .footer-legal a {
    font-size: 0.78rem; color: rgba(247,243,238,0.28);
    text-decoration: none; transition: color 0.2s;
  }
  .footer-legal a:hover { color: var(--gold); }

  @media (max-width: 1024px) {
    .hero { grid-template-columns: 1fr; }
    .hero-left { padding: 4rem 2rem 3rem; }
    .hero-right { padding: 3rem 2rem; min-height: 380px; }
    .about-section { grid-template-columns: 1fr; padding: 5rem 2rem; gap: 2.5rem; }
    .how-section { padding: 5rem 2rem; }
    .how-grid { grid-template-columns: 1fr; }
    .serve-section { padding: 5rem 2rem; }
    .serve-grid { grid-template-columns: 1fr; }
    .testi-section { padding: 5rem 2rem; }
    .testi-grid { grid-template-columns: 1fr; }
    .cta-section { padding: 5rem 2rem; }
    .footer { padding: 3rem 2rem 2rem; }
    .footer-top { grid-template-columns: 1fr; gap: 2rem; }
    .nav { padding: 1rem 1.5rem; }
    .nav.scrolled { padding: 0.75rem 1.5rem; }
  }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
const marqueeItems = [
  "Hair Styling", "·", "Nail Technology", "·", "Skincare & Aesthetics", "·",
  "Makeup Artistry", "·", "Barbering", "·", "Spa & Wellness", "·",
  "Hospitality", "·", "Beauty Education", "·", "Salon Management", "·",
  "Hair Styling", "·", "Nail Technology", "·", "Skincare & Aesthetics", "·",
  "Makeup Artistry", "·", "Barbering", "·", "Spa & Wellness", "·",
  "Hospitality", "·", "Beauty Education", "·", "Salon Management", "·",
];

const steps = [
  {
    n: "01",
    title: "Set up your profile",
    text: "Employers post their business and open roles. Professionals build a profile that shows off their skills and specialisations — not just a CV.",
  },
  {
    n: "02",
    title: "Find what fits",
    text: "Browse roles tailored to the beauty and hospitality world, or let relevant listings come to you. Filter by location, discipline, and availability.",
  },
  {
    n: "03",
    title: "Make the connection",
    text: "Apply, shortlist, and communicate — all in one place. From first contact to confirmed hire, we keep the process simple and human.",
  },
];

const employers = [
  "Salons and barbershops",
  "Spas and wellness centres",
  "Aesthetic and skincare clinics",
  "Beauty brands and distributors",
  "Beauty shops and cosmetics retailers",
  "Restaurants, hotels and resorts",
  "Training institutions",
];

const jobSeekers = [
  "Beauty therapists and aestheticians",
  "Nail technicians and barbers",
  "Makeup artists",
  "Beauty advisors and sales staff",
  "Receptionists and front-office staff",
  "Trainers and educators",
  "Hospitality professionals",
  "Students and recent graduates",
];

const testimonials = [
  {
    text: "Within two weeks of posting, I had three qualified stylists apply. Rembolist understands our industry in a way generic job boards simply don't.",
    name: "Amara Osei",
    role: "Salon owner, Nairobi",
    initials: "AO",
  },
  {
    text: "I'd been freelancing for years but wanted something steadier. Rembolist matched me with a spa that fit my specialty perfectly. Life-changing.",
    name: "Fatima Al-Rashidi",
    role: "Esthetician",
    initials: "FA",
  },
  {
    text: "Every applicant actually understood the industry. We hired our head nail technician within a month — something I couldn't manage on other platforms.",
    name: "Chloe Mwangi",
    role: "Spa director",
    initials: "CM",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = css;
    document.head.appendChild(style);

    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);

    return () => {
      document.head.removeChild(style);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <>
      {/* NAV */}
      <nav className={`nav${scrolled ? " scrolled" : ""}`}>
        <Link to="/" className="nav-logo">
          Rembo<span>List</span>
        </Link>
        <div className="nav-links">
          <a href="#about">About</a>
          <a href="#how">How It Works</a>
          <a
            href="#"
            className="nav-cta"
            onClick={(e) => {
              e.preventDefault();
              navigate("/signin");
            }}
          >
            Sign In
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-left">
          <div className="tag">Beauty · Wellness · Hospitality</div>
          <h1 className="hero-title">
            Where the right people
            <br />
            find the right <em>places</em>
          </h1>
          <p className="hero-body">
            Rembolist is a dedicated job board for the beauty, wellness, and
            hospitality industries — built to make hiring less painful and job
            searching far more targeted. No more irrelevant listings.
          </p>
          <div className="hero-actions">
            <button
              className="btn-dark"
              onClick={() => navigate("/dashboard")}
            >
              Find Work
            </button>
            <button
              className="btn-line"
              onClick={() => navigate("/employer/dashboard")}
            >
              Post a Job
            </button>
          </div>
        </div>

        <div className="hero-right">
          <div className="hero-right-label">Who we connect</div>
          <div className="serve-group">
            <div className="serve-group-title">Employers</div>
            <div className="serve-items">
              {employers.map((item) => (
                <div key={item} className="serve-item">{item}</div>
              ))}
            </div>
          </div>
          <hr className="serve-divider" />
          <div className="serve-group">
            <div className="serve-group-title">Professionals</div>
            <div className="serve-items">
              {jobSeekers.slice(0, 5).map((item) => (
                <div key={item} className="serve-item">{item}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="strip">
        <div className="strip-inner">
          {marqueeItems.map((item, i) => (
            <span key={i} className={item === "·" ? "dot" : ""}>{item}</span>
          ))}
        </div>
      </div>

      {/* ABOUT */}
      <section className="about-section" id="about">
        <div>
          <div className="section-tag">About Rembolist</div>
          <h2 className="section-title">
            A job board built for <em>this</em> industry
          </h2>
          <p className="section-body">
            Rembolist is a specialised platform for the beauty, wellness, and
            hospitality sectors. Generic job boards are built for everyone —
            which means they're rarely built for you. We exist to fix that gap:
            giving businesses a faster way to find qualified professionals, and
            giving job seekers a space where their skills are actually
            understood.
            <br />
            <br />
            Whether you're a salon owner tired of CVs that don't fit, or a
            beauty therapist looking for something more than a broad search
            engine, Rembolist was made with you in mind.
          </p>
        </div>
        <div>
          <div className="mission-block">
            <p className="mission-quote">
              Our mission is simple: to make hiring easier and help talented
              professionals connect with the{" "}
              <strong>right opportunities</strong> — not just any opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-section" id="how">
        <div className="section-tag">How It Works</div>
        <h2 className="section-title">
          Straightforward, <em>by design</em>
        </h2>
        <div className="how-grid">
          {steps.map((s) => (
            <div key={s.n} className="step-card">
              <div className="step-num">{s.n}</div>
              <div className="step-line" />
              <div className="step-title">{s.title}</div>
              <p className="step-text">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="serve-section">
        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            className="section-tag"
            style={{ color: "rgba(184,146,74,0.75)" }}
          >
            Who We Serve
          </div>
          <h2
            className="section-title"
            style={{ color: "var(--paper)", maxWidth: "540px" }}
          >
            Two sides of the same <em>industry</em>
          </h2>
          <div className="serve-grid">
            <div className="serve-card">
              <div className="serve-role">For Employers</div>
              <div className="serve-card-title">Businesses &amp; Institutions</div>
              <p className="serve-card-body">
                Stop sorting through applications that don't match. Post your
                roles once and reach professionals who actually work in your
                field — from independent salons to hotel groups.
              </p>
              <ul className="serve-list">
                {employers.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="serve-card">
              <div className="serve-role">For Job Seekers</div>
              <div className="serve-card-title">
                Beauty &amp; Hospitality Professionals
              </div>
              <p className="serve-card-body">
                Find roles that actually match your background — whether you're
                just starting out, switching specialisations, or looking for
                something senior.
              </p>
              <ul className="serve-list">
                {jobSeekers.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testi-section">
        <div className="section-tag">Success Stories</div>
        <h2 className="section-title">
          Heard from the <em>community</em>
        </h2>
        <div className="testi-grid">
          {testimonials.map((t) => (
            <div key={t.name} className="testi-card">
              <div className="testi-open">"</div>
              <p className="testi-text">{t.text}</p>
              <div className="testi-author">
                <div className="testi-avatar">{t.initials}</div>
                <div>
                  <div className="testi-name">{t.name}</div>
                  <div className="testi-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <div
            className="section-tag"
            style={{ justifyContent: "center", display: "flex" }}
          >
            Get started
          </div>
          <h2 className="cta-title">
            Your next step
            <br />
            starts <em>here</em>
          </h2>
          <p className="cta-body">
            Whether you're building a team or building a career, Rembolist is
            the dedicated platform the beauty, wellness, and hospitality
            industries have needed.
          </p>
          <div className="cta-buttons">
            <button
              className="btn-gold"
              onClick={() => navigate("/dashboard")}
            >
              I'm looking for work
            </button>
            <button
              className="btn-ghost"
              onClick={() => navigate("/employer/dashboard")}
            >
              I'm hiring talent
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-top">
          <div>
            <div className="footer-logo">
              Rembo<span>List</span>
            </div>
            <p className="footer-tagline">
              The dedicated job board for beauty, wellness, and hospitality
              across East Africa.
            </p>
          </div>
          <div>
            <div className="footer-col-title">Platform</div>
            <ul className="footer-links">
              <li><Link to="/dashboard">Browse Jobs</Link></li>
              <li><Link to="/employer/dashboard">Post a Job</Link></li>
              <li><a href="#about">About Us</a></li>
              {/* <li><a href="#">Contact</a></li> */}
            </ul>
          </div>
          {/* <div>
            <div className="footer-col-title">Support</div>
            <ul className="footer-links">
              <li><a href="#">Help Centre</a></li>
              <li><a href="#">For Employers</a></li>
              <li><a href="#">For Job Seekers</a></li>
              <li><a href="#">Safety</a></li>
            </ul>
          </div> */}
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">© 2026 Rembolist. All rights reserved.</div>
          <div className="footer-legal">
            {/* <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a> */}
          </div>
        </div>
      </footer>
    </>
  );
}